import React, {
  createContext,
  forwardRef,
  MouseEvent,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { ICellRendererReactComp } from "ag-grid-react";
import { KeyboardArrowDown } from "@mui/icons-material";
import ArrowPopper from "components/app/ArrowPopper";
import { Divider, Grid } from "@mui/material";
import InvoiceTotal from "../components/InvoiceTotal";
//import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import { AOV_SUB_GROUP_MAPPING, SUB_GROUP_MAPPING } from "constant";
import { classHOC } from "components/app/ClassWrapper";

export const CollectionRevenueContext = createContext<{
  collectionColumns?: string[];
  setCollectionColumns?: React.Dispatch<React.SetStateAction<string[]>>;
  aovCollectionColumns?: string[];
  setAOVCollectionColumns?: React.Dispatch<React.SetStateAction<string[]>>;
}>({});

const CollectionRevenueCellRenderer = forwardRef<
  ICellRendererReactComp,
  ICellRendererParams & {
    collection: string[];
    type?: string;
    showAOV?: boolean;
  }
>(({ data, api, collection, type = "arr", setValue, node }, ref) => {
  useImperativeHandle(ref, () => ({
    refresh: () => false,
  }));

  const { collectionColumns, aovCollectionColumns } = useContext(CollectionRevenueContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [groupColumns, setGroupColumns] = useState<Partial<ColDef & ColGroupDef>[]>([]);

  const value = (type === "arr" ? collectionColumns : aovCollectionColumns)
    ?.filter((col) =>
      type === "arr" ? col !== "invoiceArrContactCenterCloud" : col !== "aovContactCenterCloud"
    )
    ?.reduce((a, b) => a + (Number(data?.[b]) || 0), 0);

  // const showBlank = data.pendingCount === data.subscriptionCount && !data.new;

  useEffect(() => {
    value && value !== null
      ? "$" +
        value.toLocaleString("en-US", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : "$0.00";
  }, [value, setValue, data]);

  const columns = useMemo(() => {
    return (type === "arr" ? collectionColumns : aovCollectionColumns)!
      .sort((a, b) => collection.findIndex((x) => x === a) - collection.findIndex((x) => x === b))
      .map((x) => api.getColumnDef(x));
  }, [collectionColumns, aovCollectionColumns, collection, type]);

  const nonCollabColumns = columns?.filter((c) => c?.colId?.includes("Non"));

  useEffect(() => {
    const collabGroup = columns?.filter((c) => !c?.colId?.includes("Non"));
    const result: Partial<ColDef & ColGroupDef>[] = [];
    const groupPart: string[] = [];
    collabGroup?.forEach((item: any) => {
      if (item?.subGroupHeader || item?.subGroupHead) {
        // only for cloud contact center group, other subGroupHeader only for sort order
        if (
          type === "arr"
            ? item?.subGroupHeader === "Invoice ARR (Cloud Contact Center)" ||
              item?.subGroupHead === "Invoice ARR (Cloud Contact Center)"
            : item?.subGroupHeader === "AOV (Cloud Contact Center)" ||
              item?.subGroupHead === "AOV (Cloud Contact Center)"
        ) {
          if (groupPart.includes(item.subGroupHeader) || groupPart.includes(item.subGroupHead)) {
            const curItem = result.filter(
              (r) => r.headerName === item?.subGroupHeader || r.headerName === item?.subGroupHead
            );
            if (curItem?.length) {
              curItem?.[0]?.children?.push({
                colId: item?.colId,
                headerName: item?.headerName,
              });
            }
          } else {
            const curItem: any = {
              colId: (type === "arr" ? SUB_GROUP_MAPPING : AOV_SUB_GROUP_MAPPING).filter(
                (i) =>
                  i.groupHeader === item?.subGroupHeader || i.groupHeader === item?.subGroupHead
              )?.[0]?.colId,
              headerName: item?.subGroupHeader || item.subGroupHead,
              children: [],
            };
            curItem.children.push({
              colId: item?.colId,
              headerName: item?.headerName,
            });
            groupPart.push(item.subGroupHeader || item.subGroupHead);
            result.push(curItem);
          }
        } else {
          result.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
        }
      } else {
        if (
          (type === "arr" ? SUB_GROUP_MAPPING : AOV_SUB_GROUP_MAPPING).find(
            (i) => i?.colId !== item?.colId
          )
        )
          result.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
      }
    });
    setGroupColumns(result);
    // console.log(result);
  }, [columns]);

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  if (node.rowPinned) {
    return <InvoiceTotal {...{ api, data, value }} />;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        ref={rootRef}
        onClick={handleClick}
      >
        $
        {value && value !== null
          ? value.toLocaleString("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })
          : "0.00"}
        <KeyboardArrowDown style={{ fontSize: "1rem" }} />
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right"
          disablePortal={false}
          style={{ zIndex: status ? 0 : 2000 }}
        >
          <div ref={popRef}>
            <Grid
              container={true}
              direction="column"
            >
              {columns.length ? (
                <>
                  {groupColumns?.length ? (
                    <>
                      <Grid
                        item={true}
                        style={{
                          color: "#333",
                          fontSize: 14,
                          lineHeight: "14px",
                          fontWeight: 700,
                          marginBottom: 10,
                        }}
                      >
                        Collab
                      </Grid>
                      <>
                        {groupColumns?.map((col: Partial<ColDef & ColGroupDef>, i) => (
                          <div key={col?.colId}>
                            {!!col?.children?.length && <Divider style={{ margin: "5px 0" }} />}
                            <Grid
                              item={true}
                              container={true}
                              key={col.colId! + i}
                              justifyContent="space-between"
                              style={{
                                marginTop: i === 0 ? 0 : 4,
                              }}
                              alignItems="center"
                            >
                              <Grid
                                item={true}
                                style={{
                                  color: "#545454",
                                  fontSize: 14,
                                  lineHeight: "19px",
                                }}
                              >
                                {col.headerName!.split(/\((.*)\)/)?.[1] === "Last 3 Years"
                                  ? "Total Device Bookings $$ (Last 3 Years)"
                                  : col.headerName!.split(/\((.*)\)/)?.[1]}
                              </Grid>
                              <Grid
                                item={true}
                                style={{
                                  color: "#333",
                                  fontSize: 14,
                                  lineHeight: "19px",
                                  marginLeft: 16,
                                  fontWeight: 500,
                                }}
                              >
                                {`$${(
                                  (data[col.field! || col.colId!] as number) || 0
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                              </Grid>
                            </Grid>
                            {!!col?.children?.length && (
                              <Grid
                                item={true}
                                container={true}
                                key={col?.colId}
                                justifyContent="space-between"
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  marginTop: i === 0 ? 0 : 4,
                                }}
                                alignItems="center"
                              >
                                {col?.children?.map((child: Partial<ColDef & ColGroupDef>) => (
                                  <div
                                    key={child.colId!}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      width: "100%",
                                    }}
                                  >
                                    <Grid
                                      item={true}
                                      style={{
                                        color: "#898989",
                                        fontSize: 12,
                                        lineHeight: "16px",
                                      }}
                                    >
                                      {child.headerName!.split(/\((.*)\)/)?.[1]}
                                    </Grid>
                                    <Grid
                                      item={true}
                                      style={{
                                        color: "#333",
                                        fontSize: 12,
                                        lineHeight: "14px",
                                        marginLeft: 16,
                                        fontWeight: 500,
                                      }}
                                    >
                                      $
                                      {(
                                        (data[child.field! || child.colId!] as number) || 0
                                      ).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </Grid>
                                  </div>
                                ))}
                              </Grid>
                            )}
                            {!!col?.children?.length && <Divider style={{ margin: "5px 0" }} />}
                          </div>
                        ))}
                      </>
                      <br />
                    </>
                  ) : null}
                  {nonCollabColumns?.length ? (
                    <>
                      <Grid
                        item={true}
                        style={{
                          color: "#333",
                          fontSize: 14,
                          lineHeight: "14px",
                          fontWeight: 700,
                          marginBottom: 10,
                        }}
                      >
                        Non-Collab
                      </Grid>
                      {nonCollabColumns?.map((col, i) => (
                        <Grid
                          item={true}
                          container={true}
                          key={col.colId!}
                          justifyContent="space-between"
                          style={{ marginTop: i === 0 ? 0 : 4 }}
                          alignItems="center"
                        >
                          <Grid
                            item={true}
                            style={{
                              color: "#545454",
                              fontSize: 14,
                              lineHeight: "19px",
                            }}
                          >
                            {col.headerName!.split(/\((.*)\)/)?.[1]}
                          </Grid>
                          <Grid
                            item={true}
                            style={{
                              color: "#333",
                              fontSize: 14,
                              lineHeight: "19px",
                              marginLeft: 16,
                              fontWeight: 500,
                            }}
                          >
                            $
                            {((data[col.field! || col.colId!] as number) || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </>
                  ) : null}
                </>
              ) : (
                <span>No column selected</span>
              )}
            </Grid>
          </div>
        </ArrowPopper>
      ) : null}
    </>
  );
});

export default classHOC(CollectionRevenueCellRenderer);
